import { areArrayEquals, createEqualityChecker, isArrayNotEmpty, objectKeys } from '../../../../../utils'

import { type Capability, ConnectwareError, type ConnectwareErrorExtras, ConnectwareErrorType, type PaginatedData } from '../../../../../domain'

import type {
    PageSubscriptionsTypes,
    SingleSubscriptionsTypes,
    SubscriptionFilterArgs,
    SubscriptionPageEvent,
    SubscriptionPageOptions,
} from '../../../../../application'

import type { BackendHttpMethod, BackendPath, RSTAdapter } from '../../../../Connectware'
import type { HttpRequestArgs, HttpResponse } from '../../..'
import type { SubscriptionStrategy } from '..'

export abstract class BaseSubscriptionStrategy<P extends keyof PageSubscriptionsTypes, S extends keyof SingleSubscriptionsTypes>
    implements SubscriptionStrategy<P, S>
{
    protected readonly supportedFilters: (keyof SubscriptionFilterArgs)[] | null = null

    abstract readonly requiredPermissions: readonly Capability[]

    readonly supportsPagination: boolean = false

    /**
     * Comparison helper to deal with possible errors being in the results
     */
    private static compare<R> (oldResult: R | ConnectwareError, newResult: R | ConnectwareError, comparator: (oldResult: R, newResult: R) => boolean): boolean {
        return ConnectwareError.is(oldResult) || ConnectwareError.is(newResult) ? oldResult === newResult : comparator(oldResult, newResult)
    }

    private static createErrorHandler (error: ConnectwareError): () => Promise<never> {
        return () => Promise.reject(error)
    }

    protected static createServerErrorHandler (message: string, extras?: ConnectwareErrorExtras<ConnectwareError>): () => Promise<never> {
        return this.createErrorHandler(new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, message, extras))
    }

    protected static createServerNotAvailableHandler (message: string, extras?: ConnectwareErrorExtras<ConnectwareError>): () => Promise<never> {
        return this.createErrorHandler(new ConnectwareError(ConnectwareErrorType.SERVER_NOT_AVAILABLE, message, extras))
    }

    protected static createBadRequestHandler (action: string): () => Promise<never> {
        return this.createErrorHandler(new ConnectwareError(ConnectwareErrorType.BAD_REQUEST, `Bad request detected while ${action}`))
    }

    protected static createJsonHandler<R> (): (response: Readonly<{ getJson(): Promise<R> }>) => Promise<R> {
        return (response) => response.getJson()
    }

    protected static createNotFoundErrorHandler (
        path: string,
        id: string
    ): (response: Pick<HttpResponse<number, unknown>, 'getText'>) => Promise<ConnectwareError> {
        return (response) =>
            response.getText().then(
                (text) =>
                    new ConnectwareError(ConnectwareErrorType.NOT_FOUND, 'The endpoint yielded a 404 result', {
                        path,
                        pathParams: [id],
                        text,
                    })
            )
    }

    constructor (
        protected readonly request: <Path extends BackendPath, Method extends BackendHttpMethod<Path>, Return>(
            args: HttpRequestArgs<Path, Method, Return>
        ) => Promise<Return>,
        protected readonly rstAdapter: RSTAdapter
    ) {}

    private areArrayEquals (aArr: readonly PageSubscriptionsTypes[P][], bArr: readonly PageSubscriptionsTypes[P][]): boolean {
        return areArrayEquals(aArr, bArr, { sort: false, equals: (a, b) => this.areEquals(a, b) })
    }

    protected abstract areEquals (
        oldResult: PageSubscriptionsTypes[P] | SingleSubscriptionsTypes[S],
        newResult: PageSubscriptionsTypes[P] | SingleSubscriptionsTypes[S]
    ): boolean

    supportsFilters (filter: SubscriptionFilterArgs): boolean {
        const { supportedFilters } = this
        const presentFilters = objectKeys(filter)

        return !isArrayNotEmpty(presentFilters) || Boolean(supportedFilters && presentFilters.every((f) => supportedFilters.includes(f)))
    }

    retrieveAll (filter: SubscriptionFilterArgs): Promise<PageSubscriptionsTypes[P][]> {
        throw new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Method not implemented', { method: 'retrieveAll', filter })
    }

    retrievePage (options: SubscriptionFilterArgs & SubscriptionPageOptions<P>): Promise<PaginatedData<PageSubscriptionsTypes[P]>> {
        throw new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Method not implemented', { method: 'retrievePage', options })
    }

    retrieveOne (id: string): Promise<SingleSubscriptionsTypes[S]> {
        throw new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Method not implemented', { method: 'retrieveOne', id })
    }

    retrieveCount (): Promise<number> {
        throw new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Method not implemented', { method: 'retrieveCount' })
    }

    compareOne (oldResult: SingleSubscriptionsTypes[S] | ConnectwareError, newResult: SingleSubscriptionsTypes[S] | ConnectwareError): boolean {
        return BaseSubscriptionStrategy.compare(oldResult, newResult, (a, b) => this.areEquals(a, b))
    }

    compareAll (oldResults: PageSubscriptionsTypes[P][] | ConnectwareError, newResults: PageSubscriptionsTypes[P][] | ConnectwareError): boolean {
        return BaseSubscriptionStrategy.compare(oldResults, newResults, (a, b) => this.areArrayEquals(a, b))
    }

    comparePage (oldPage: SubscriptionPageEvent<P>, newPage: SubscriptionPageEvent<P>): boolean {
        const arePageDataEquals = createEqualityChecker<PaginatedData<PageSubscriptionsTypes[P]>>({
            totalCount: null,
            pageSize: null,
            page: null,
            current: (a, b) => this.areArrayEquals(a, b),
        })

        return BaseSubscriptionStrategy.compare(oldPage, newPage, (a, b) => arePageDataEquals(a, b))
    }
}
